import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { generateDateTicks } from '../../../../services/dataTicks';

const PropRPMvsMCR = ({ data }) => {
  console.log("RpmVsMcrData:", data);
  
  // Filter out dates with zero data and prepare chart data
  const dataArray = Array.isArray(data) ? data : [data];

  const validData = dataArray.filter(item => (Number(item.rpm) || 0) > 0 && (Number(item.mcrPercent) || 0) > 0);

  console.log("validData:", validData);

  const chartData = validData.map(item => ({
    date: item.date || new Date().toISOString().split('T')[0],
    mcr: Number(item.mcr) || Number(item.mcrPercent) || 0,
    rpm: Number(item.rpm) || 0
  }));

  console.log("ChartData:", chartData);

  const dates = chartData.length > 0 ? chartData.map(item => item.date) : [];
  let tickPositions = generateDateTicks(dates);

  // Prepare chart series data - both as scatter
  const rpmSeries = chartData.map(item => [
    new Date(item.date).getTime(),
    item.rpm
  ]);

  const mcrSeries = chartData.map(item => [
    new Date(item.date).getTime(),
    item.mcr
  ]);

  // Calculate trend line only for valid data points
  const calculateTrendLine = (seriesData) => {
    if (seriesData.length < 2) return [];

    // Use day indices instead of timestamps to avoid precision issues
    const n = seriesData.length;
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;

    seriesData.forEach((point, index) => {
      const x = index; // Use day index instead of timestamp
      const y = point[1]; // value
      sumX += x;
      sumY += y;
      sumXY += x * y;
      sumX2 += x * x;
    });

    // Calculate slope (m) and intercept (b) for y = mx + b
    const m = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const b = (sumY - m * sumX) / n;

    // Create trend line points using original timestamps
    return seriesData.map((point, index) => [
      point[0], // original timestamp
      m * index + b // calculated y value
    ]);
  };

  // Calculate combined trend line that represents the relationship
  const calculateCombinedTrendLine = () => {
    if (chartData.length < 2) return [];

    // Use RPM trend as the primary trend since it's more sensitive to changes
    const rpmTrend = calculateTrendLine(rpmSeries);
    
    return rpmTrend;
  };

  const combinedTrendData = calculateCombinedTrendLine();

  const chartOptions = {
    chart: {
      type: 'scatter',
      height: '500px',
      backgroundColor: 'transparent',
      spacing: [20, 15, 15, 15]
    },
    title: {
      text: null
    },
    xAxis: {
      type: 'datetime',
      tickPositions: tickPositions,
      title: {
        text: null,
      },
      labels: {
        format: '{value:%b %e}',
        style: {
          color: '#666666'
        }
      },
      gridLineWidth: 1,
      gridLineColor: '#f0f0f0',
    },
    yAxis: [
      {
        title: {
          text: '% MCR',
          style: {
            color: '#1a5276',
            fontSize: '12px'
          }
        },
        min: 0,
        max: 150,
        tickInterval: 20,
        gridLineWidth: 1,
        gridLineColor: '#f0f0f0',
        labels: {
          style: {
            color: '#666666'
          }
        },
        opposite: false // MCR on left side
      },
      {
        title: {
          text: 'Avg Prop RPM (rpm)',
          style: {
            color: '#7d3c98',
            fontSize: '12px'
          }
        },
        min: 0,
        max: 150,
        tickInterval: 20,
        gridLineWidth: 0,
        labels: {
          style: {
            color: '#666666'
          }
        },
        opposite: true // RPM on right side
      }
    ],
    legend: {
      align: 'right',
      verticalAlign: 'top',
      itemStyle: {
        color: '#666666',
        fontWeight: 'normal'
      },
      itemHoverStyle: {
        color: '#333333'
      },
      symbolRadius: 0
    },
    tooltip: {
      shared: true,
      crosshairs: true,
      backgroundColor: '#ffffff',
      borderColor: '#e5e7eb',
      borderRadius: 6,
      shadow: true,
      useHTML: true,
      headerFormat:
        '<div style="font-weight:600;font-size:12px;margin-bottom:5px">{point.x:%b %e, %Y}</div>',
      pointFormat:
        '<div style="display:flex;justify-content:space-between"><span style="color:{series.color}">{series.name}:</span><span style="font-weight:700;margin-left:10px">{point.y}</span></div>',
    },
    plotOptions: {
      scatter: {
        marker: {
          radius: 5,
          symbol: 'circle',
          lineWidth: 2,
          lineColor: '#ffffff',
          states: {
            hover: {
              enabled: true,
              lineColor: '#333333',
              radius: 6,
            },
          },
        },
        states: {
          hover: {
            halo: {
              size: 8,
            },
          },
        },
      },
      line: {
        marker: {
          enabled: false,
        },
        states: {
          hover: {
            halo: {
              size: 0,
            },
          },
        },
      },
    },
    series: [
      {
        name: '% MCR',
        type: 'scatter',
        data: mcrSeries,
        color: '#1a5276',
        yAxis: 0,
        marker: {
          fillColor: '#1a5276',
          lineWidth: 2,
          lineColor: '#ffffff',
          radius: 5,
        },
        tooltip: {
          valueSuffix: ' %',
        },
      },
      {
        name: 'Propeller RPM',
        type: 'scatter',
        data: rpmSeries,
        color: '#7d3c98',
        yAxis: 1,
        marker: {
          fillColor: '#7d3c98',
          lineWidth: 2,
          lineColor: '#ffffff',
          radius: 5,
        },
        tooltip: {
          valueSuffix: ' rpm',
        },
      },
      {
        name: 'Performance Trend',
        type: 'line',
        data: combinedTrendData,
        color: '#e74c3c',
        yAxis: 1,
        lineWidth: 2,
        dashStyle: 'Dash',
        marker: {
          enabled: false,
        },
        states: {
          hover: {
            lineWidth: 3,
          },
        },
        tooltip: {
          valueSuffix: ' rpm',
          pointFormat: '<div style="display:flex;justify-content:space-between"><span style="color:{series.color}">{series.name}:</span><span style="font-weight:700;margin-left:10px">{point.y:.1f}</span></div>'
        },
      }
    ],
    credits: {
      enabled: false
    },
    responsive: {
      rules: [{
        condition: {
          maxWidth: 600
        },
        chartOptions: {
          legend: {
            align: 'center',
            verticalAlign: 'bottom',
            layout: 'horizontal'
          },
          xAxis: {
            labels: {
              format: '{value:%b %e}',
            },
          },
        }
      }]
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="flex justify-between items-center">
        <h1>Prop. RPM & % MCR over Time</h1>
      </div>

      {chartData.length > 0 ? (
        <HighchartsReact
          highcharts={Highcharts}
          options={chartOptions}
          containerProps={{
            className: 'w-full',
            style: { height: '500px' }
          }}
        />
      ) : (
        <div className="flex items-center justify-center h-96">
          <p className="text-gray-500 text-lg">No data available for the selected period</p>
        </div>
      )}
    </div>
  );
};

export default PropRPMvsMCR;